import { Link } from 'react-router-dom';
import { BRAND } from '@/config/brand';
import LegalPageLayout, { formatAddress } from '@/components/layout/LegalPageLayout';

export default function Privacy() {
  return (
    <LegalPageLayout title="Privacy Policy">
      <section>
        <h2>1. Introduction</h2>
        <p>
          This Privacy Policy explains how {BRAND.legalName} (&ldquo;{BRAND.name},&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
          collects, uses, shares, and protects personal information when you visit or use our websites, map search,
          mobile experiences, and related services (collectively, the &ldquo;Services&rdquo;).
        </p>
        <p>
          We operate a real-estate discovery platform that helps consumers explore listings, save homes, and connect with
          agents and advertisers. Our principal place of business is {formatAddress()}.
        </p>
      </section>

      <section>
        <h2>2. Information we collect</h2>
        <p>Depending on how you use the Services, we may collect:</p>
        <ul>
          <li>
            <strong>Account information</strong> — name, email address, password, phone number, and profile details you
            provide when you register or update an account.
          </li>
          <li>
            <strong>Search and saved activity</strong> — saved homes, saved searches, alerts, filters, favorites, and
            similar preferences that help personalize your experience.
          </li>
          <li>
            <strong>Listing inquiries and communications</strong> — messages, tour requests, lead forms, and other
            content you submit when contacting agents, advertisers, or {BRAND.name} support.
          </li>
          <li>
            <strong>Device and usage data</strong> — IP address, browser type, device identifiers, pages viewed, referring
            URLs, approximate location derived from IP, and interaction data collected through logs and analytics.
          </li>
          <li>
            <strong>Cookies and similar technologies</strong> — as described in our{' '}
            <Link to="/cookies">Cookie Policy</Link>.
          </li>
          <li>
            <strong>Location data</strong> — precise location if you allow map geolocation features in your browser or
            device settings.
          </li>
          <li>
            <strong>Payment and advertising-related data</strong> — limited billing or account details when you purchase
            advertising or other paid products (processed by our payment providers as applicable).
          </li>
        </ul>
      </section>

      <section>
        <h2>3. How we use information</h2>
        <p>We use personal information to:</p>
        <ul>
          <li>Provide, operate, and improve map search, listings, saved homes, alerts, and account features;</li>
          <li>Personalize content and recommendations based on your activity and preferences;</li>
          <li>Route inquiries and facilitate contact with listing agents, brokers, or advertisers you choose to contact;</li>
          <li>Offer and manage advertising, sponsorship, and related products for professionals;</li>
          <li>Communicate about your account, service updates, and (where permitted) marketing;</li>
          <li>Monitor security, prevent fraud and abuse, and enforce our Terms of Use;</li>
          <li>Comply with law, respond to lawful requests, and protect our rights and users.</li>
        </ul>
      </section>

      <section>
        <h2>4. How we share information</h2>
        <p>We may share information in the following circumstances:</p>
        <ul>
          <li>
            <strong>Service providers</strong> — vendors that host, analyze, send email/SMS, process payments, provide
            customer support tooling, or otherwise help us operate the Services under contractual confidentiality
            obligations.
          </li>
          <li>
            <strong>MLS / IDX partners</strong> — listing data and related display obligations may involve MLS and IDX
            data providers as needed to show property information; your use of listing data remains subject to applicable
            display rules and our Terms.
          </li>
          <li>
            <strong>Agents and advertisers</strong> — when you submit a lead, inquiry, or tour request, we share the
            information you provide with the relevant agent, brokerage, or advertiser so they can respond.
          </li>
          <li>
            <strong>Legal and safety</strong> — when required by law, legal process, or to protect rights, safety, and
            security.
          </li>
          <li>
            <strong>Business transfers</strong> — in connection with a merger, acquisition, financing, reorganization, or
            sale of assets, subject to appropriate confidentiality protections.
          </li>
        </ul>
        <p>
          We do not sell personal information for money in the traditional sense. Like many online services, we may use
          advertising and analytics technologies that involve sharing limited identifiers with partners for measurement
          or interest-based advertising. Where required, you may opt out of certain &ldquo;sale&rdquo; or &ldquo;sharing&rdquo;
          of personal information under California law through the choices described below or by contacting us.
        </p>
      </section>

      <section>
        <h2>5. Cookies and similar technologies</h2>
        <p>
          We and our partners use cookies, local storage, and similar technologies to keep you signed in, remember
          preferences, measure performance, and support advertising features. For details and management options, see our{' '}
          <Link to="/cookies">Cookie Policy</Link>.
        </p>
      </section>

      <section>
        <h2>6. Your choices and rights</h2>
        <p>Depending on your location, you may have rights to:</p>
        <ul>
          <li>Access, correct, or delete certain personal information;</li>
          <li>Opt out of promotional emails (unsubscribe links are included in those messages);</li>
          <li>Disable non-essential cookies through browser settings or our Cookie Policy guidance;</li>
          <li>Withdraw geolocation permission in your device or browser settings;</li>
          <li>Request information about how we collect and use personal information.</li>
        </ul>
        <h3>California privacy notice (summary)</h3>
        <p>
          California residents may have rights under the CCPA/CPRA to know, delete, correct, and opt out of the sale or
          sharing of personal information, and to not be discriminated against for exercising those rights. Categories of
          information we may collect include identifiers, commercial information (such as inquiries), internet/electronic
          activity, geolocation data (if enabled), and inferences drawn from activity. To exercise rights, contact us at{' '}
          <a href={`mailto:${BRAND.support.email}`}>{BRAND.support.email}</a> or via our{' '}
          <Link to="/contact">contact page</Link>. We will verify requests as required by law.
        </p>
      </section>

      <section>
        <h2>7. Data retention and security</h2>
        <p>
          We retain personal information for as long as needed to provide the Services, comply with legal obligations,
          resolve disputes, and enforce agreements. Retention periods vary by data type and use case.
        </p>
        <p>
          We use administrative, technical, and organizational measures designed to protect personal information.
          No method of transmission or storage is completely secure; please use a strong, unique password and notify us
          promptly of any suspected unauthorized account access.
        </p>
      </section>

      <section>
        <h2>8. Children&apos;s privacy</h2>
        <p>
          The Services are not directed to children under 16, and we do not knowingly collect personal information from
          children under 16. If you believe a child has provided us personal information, contact us and we will take
          appropriate steps to delete it.
        </p>
      </section>

      <section>
        <h2>9. International users</h2>
        <p>
          The Services are operated from the United States. If you access the Services from outside the U.S., you
          understand that your information may be processed in the United States and other jurisdictions that may have
          different data-protection laws than your country of residence.
        </p>
      </section>

      <section>
        <h2>10. Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we will revise the effective date above and,
          where appropriate, provide additional notice. Continued use of the Services after an update means you
          acknowledge the revised policy.
        </p>
      </section>

      <section>
        <h2>11. Contact us</h2>
        <p>
          Questions about this Privacy Policy or our privacy practices may be sent to{' '}
          <a href={`mailto:${BRAND.support.email}`}>{BRAND.support.email}</a> or through our{' '}
          <Link to="/contact">contact form</Link>.
        </p>
        <p>
          {BRAND.legalName}
          <br />
          {formatAddress()}
        </p>
      </section>
    </LegalPageLayout>
  );
}
